import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { conditionalDevtools } from '@/lib/storeMiddleware';

type AttachmentStoreKey = `${string}:${string}:${string}`;

interface AttachmentBlobCacheEntry {
  blob: Blob;
  filename?: string | null;
  mimeType?: string | null;
  size?: number | null;
  createdAt: number;
}

interface AttachmentStoreState {
  downloadedByEmail: Record<string, Set<string>>;
  persistTimer: number | null;
  blobCache: Map<AttachmentStoreKey, AttachmentBlobCacheEntry>;

  markDownloaded: (accountId: string, emailId: string, attachmentId: string) => void;
  isAttachmentDownloaded: (accountId: string, emailId: string, attachmentId: string) => boolean;
  schedulePersist: () => void;
  setAttachmentBlob: (
    accountId: string,
    emailId: string,
    attachmentId: string,
    entry: Omit<AttachmentBlobCacheEntry, 'createdAt'>,
  ) => void;
  getAttachmentBlob: (
    accountId: string,
    emailId: string,
    attachmentId: string,
  ) => AttachmentBlobCacheEntry | undefined;
  removeAttachmentBlob: (accountId: string, emailId: string, attachmentId: string) => void;
}

interface AttachmentStorePersistedState {
  downloadedByEmail: Record<string, string[]>;
}

const MAX_DOWNLOADED_ENTRIES = 500;

const setsToArrays = (record: Record<string, Set<string>>): Record<string, string[]> => {
  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => [key, Array.from(value)])
  );
};

const arraysToSets = (record: Record<string, string[]> = {}): Record<string, Set<string>> => {
  const entries = Object.entries(record);
  // Evict oldest entries if over the limit (keys are insertion-ordered)
  const trimmed = entries.length > MAX_DOWNLOADED_ENTRIES
    ? entries.slice(entries.length - MAX_DOWNLOADED_ENTRIES)
    : entries;
  return Object.fromEntries(
    trimmed.map(([key, value]) => [key, new Set(value)])
  );
};

export const useAttachmentStore = create<AttachmentStoreState>()(
  conditionalDevtools(
    persist(
      (set, get) => ({
        downloadedByEmail: {},
        persistTimer: null,
        blobCache: new Map<AttachmentStoreKey, AttachmentBlobCacheEntry>(),

        markDownloaded: (accountId, emailId, attachmentId) => {
          const key = `${accountId}:${emailId}`;
          set((state) => {
            const existing = state.downloadedByEmail[key];
            const nextSet = existing ? new Set(existing) : new Set<string>();
            nextSet.add(attachmentId);

            return {
              downloadedByEmail: {
                ...state.downloadedByEmail,
                [key]: nextSet,
              },
            };
          });

          get().schedulePersist();
        },

        isAttachmentDownloaded: (accountId, emailId, attachmentId) => {
          const key = `${accountId}:${emailId}`;
          const downloaded = get().downloadedByEmail[key];
          return downloaded ? downloaded.has(attachmentId) : false;
        },

        schedulePersist: () => {
          const { persistTimer } = get();
          if (persistTimer !== null && typeof window !== 'undefined') {
            window.clearTimeout(persistTimer);
          }

          if (typeof window === 'undefined') {
            set({ persistTimer: null });
            return;
          }

          const timer = window.setTimeout(() => {
            set({ persistTimer: null });
            // Persist handled automatically by middleware after state change
          }, 400);

          set({ persistTimer: timer });
        },

        setAttachmentBlob: (accountId, emailId, attachmentId, entry) => {
          const key: AttachmentStoreKey = `${accountId}:${emailId}:${attachmentId}`;
          set((state) => {
            const next = new Map(state.blobCache);
            next.set(key, {
              blob: entry.blob,
              filename: entry.filename ?? null,
              mimeType: entry.mimeType ?? null,
              size: entry.size ?? null,
              createdAt: Date.now(),
            });
            // Limit cache size to avoid unbounded growth
            if (next.size > 50) {
              const oldest = Array.from(next.entries()).sort(
                (a, b) => a[1].createdAt - b[1].createdAt,
              )[0];
              if (oldest) {
                next.delete(oldest[0]);
              }
            }
            return { blobCache: next };
          });
        },

        getAttachmentBlob: (accountId, emailId, attachmentId) => {
          const key: AttachmentStoreKey = `${accountId}:${emailId}:${attachmentId}`;
          return get().blobCache.get(key);
        },

        removeAttachmentBlob: (accountId, emailId, attachmentId) => {
          const key: AttachmentStoreKey = `${accountId}:${emailId}:${attachmentId}`;
          set((state) => {
            if (!state.blobCache.has(key)) {
              return state;
            }
            const next = new Map(state.blobCache);
            next.delete(key);
            return { blobCache: next };
          });
        },
      }),
      {
        name: 'attachments-storage',
        partialize: (state) =>
          ({
            downloadedByEmail: setsToArrays(state.downloadedByEmail),
          } as unknown as AttachmentStoreState),
        merge: (persistedState, currentState) => {
          const downloaded =
            (persistedState as AttachmentStorePersistedState | undefined)?.downloadedByEmail ?? {};
          return {
            ...currentState,
            downloadedByEmail: arraysToSets(downloaded),
            blobCache: new Map(),
          };
        },
      }
    ),
    { name: 'AttachmentStore' }
  )
);
